import React, { memo, useMemo, useState } from 'react';

import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Accordion from '@mui/material/Accordion';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';
import { Grid, Typography, AccordionSummary } from '@mui/material';

import useLocales from '../../hooks/useLocales';
import { getFileType } from '../../utils/getFileFormat';
import { DocumentFile } from '../documents/upload/types';
import { DocumentModuleTypes } from '../../@types/shared/enums/documentModuleTypes';
import { MultiFilePreviewDocument } from '../documents/upload/MultiFilePreviewDocument';
import DocumentViewerDialog, { DocumentViewerFile } from '../documents/DocumentViewerDialog';

// --------------------------------------------------------

export type TempFile = {
  fileName: string;
  accessId: string;
};

interface FilePreviewSectionsProps {
  mainTempFiles: TempFile[];
  attachmentsTempFiles: TempFile[];
  formModuleType: DocumentModuleTypes;
  onCombinedModeClick?: () => void;
}

const FilePreviewSections: React.FC<FilePreviewSectionsProps> = ({
  mainTempFiles,
  attachmentsTempFiles,
  formModuleType,
  onCombinedModeClick,
}) => {
  const { t } = useLocales();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<DocumentViewerFile | null>(null);

  // Map TempFile to DocumentFile format (accessId -> uri)
  const mapToDocumentFiles = (files: TempFile[]): DocumentFile[] =>
    files.map((f) => ({
      uri: f.accessId,
      fileName: f.fileName,
      fileType: getFileType(f.fileName),
    }));

  const mainFiles = useMemo(() => mapToDocumentFiles(mainTempFiles), [mainTempFiles]);
  const attachmentFiles = useMemo(
    () => mapToDocumentFiles(attachmentsTempFiles),
    [attachmentsTempFiles]
  );

  const handleFileClick = (file: DocumentFile) => {
    setSelectedFile({
      uri: file.uri,
      fileName: file.fileName || '',
      fileType: file.fileType,
    });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedFile(null);
  };

  return (
    <>
      {/* Main Document */}
      {formModuleType === DocumentModuleTypes.Accounting && mainFiles.length > 0 && (
        <Grid size={{ xs: 12 }}>
          <Accordion defaultExpanded sx={{ p: 3 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 3 }}>
              <Typography variant="h6"> {t('documents.mainDocument')}</Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ p: 3, pt: 0 }}>
              <Stack spacing={2}>
                <MultiFilePreviewDocument
                  files={mainFiles}
                  thumbnail={false}
                  onItemClick={handleFileClick}
                />

                {onCombinedModeClick && (
                  <Button variant="contained" onClick={onCombinedModeClick}>
                    {t('documents.mergedDocuments')}
                  </Button>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}

      {/* Attachments */}
      {attachmentFiles.length > 0 && (
        <Grid size={{ xs: 12 }}>
          <Accordion defaultExpanded sx={{ p: 3 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 3 }}>
              <Typography variant="h6"> {t('documents.attachments')}</Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ p: 3, pt: 0 }}>
              <MultiFilePreviewDocument
                files={attachmentFiles}
                thumbnail={false}
                onItemClick={handleFileClick}
              />
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}

      <DocumentViewerDialog open={isDialogOpen} onClose={handleCloseDialog} file={selectedFile} />
    </>
  );
};

export default memo(FilePreviewSections);
